import z from 'zod';

import type { ErpActionTarget } from './index';

export type ErpInteractionSurfaceId = 'desktop' | 'admin' | 'page';

export interface ErpInteractionSurface {
    id: ErpInteractionSurfaceId;
    section?: string;
    referentialKey?: string;
}

export interface ErpInteractionNavigationRuntime {
    route?: string;
    currentTargets?: ErpActionTarget[];
    surface?: ErpInteractionSurface;
}

export interface ErpInteractionSource {
    id: string;
    kind: 'attachment';
    name: string;
    mimeType: string;
    size?: number;
    semanticType?: string;
    origin?: 'current-turn' | 'conversation';
}

export interface ErpInteractionSourcesContext {
    sources: readonly ErpInteractionSource[];
}

export type ErpInteractionOperation = 'create' | 'update' | 'delete' | 'read' | 'search' | 'navigate' | 'execute';
export type ErpInteractionTargetMode = 'new' | 'current' | 'explicit' | 'unknown';

export interface ErpInteractionIntentContext {
    operation?: ErpInteractionOperation;
    entityType?: string;
    targetMode?: ErpInteractionTargetMode;
    explicitTarget?: {
        type?: string;
        id?: string;
        reference?: string;
    };
}

export const ErpInteractionIntentContextSchema = z.object({
    operation: z.enum(['create', 'update', 'delete', 'read', 'search', 'navigate', 'execute']).optional(),
    entityType: z.string().trim().min(1).max(80).optional(),
    targetMode: z.enum(['new', 'current', 'explicit', 'unknown']).optional(),
    explicitTarget: z.object({
        type: z.string().trim().min(1).max(80).optional(),
        id: z.string().trim().min(1).max(200).optional(),
        reference: z.string().trim().min(1).max(256).optional(),
    }).strict().optional(),
}).strict();

export type ErpInteractionRecentActionStatus =
    | 'executed'
    | 'requires-input'
    | 'requires-disambiguation'
    | 'awaiting-confirmation'
    | 'failed'
    | 'unavailable';

export interface ErpInteractionRecentAction {
    actionId: string;
    status: ErpInteractionRecentActionStatus;
    targets?: ErpActionTarget[];
    createdEntities?: {
        type: string;
        id: string;
        label?: string;
    }[];
    updatedEntities?: { type: string; id: string; label?: string }[];
    archivedEntities?: { type: string; id: string; label?: string }[];
    executedAt?: string;
}

export const ErpInteractionRecentActionSchema = z.object({
    actionId: z.string().trim().min(1).max(200),
    status: z.enum(['executed', 'requires-input', 'requires-disambiguation', 'awaiting-confirmation', 'failed', 'unavailable']),
    targets: z.array(z.object({
        type: z.string().trim().min(1).max(80),
        id: z.string().trim().min(1).max(200),
        label: z.string().max(200).optional(),
    }).strict()).max(32).optional(),
    createdEntities: z.array(z.object({
        type: z.string().trim().min(1).max(80),
        id: z.string().trim().min(1).max(200),
        label: z.string().max(200).optional(),
    }).strict()).max(16).optional(),
    updatedEntities: z.array(z.object({
        type: z.string().trim().min(1).max(80),
        id: z.string().trim().min(1).max(200),
        label: z.string().max(200).optional(),
    }).strict()).max(16).optional(),
    archivedEntities: z.array(z.object({
        type: z.string().trim().min(1).max(80),
        id: z.string().trim().min(1).max(200),
        label: z.string().max(200).optional(),
    }).strict()).max(16).optional(),
    executedAt: z.string().max(100).optional(),
}).strict();

export type ErpInteractionRecentActionContract = z.infer<typeof ErpInteractionRecentActionSchema>;

/**
 * Runtime UI hints are intentionally opaque to the Context Layer. A consumer
 * validates the subset it needs before presenting it to an LLM.
 */
export interface ErpInteractionDesktopContext {
    activeBoardId: string;
    activeWidget?: unknown;
    openedWidgets: readonly unknown[];
}

/** Runtime-only input for lazy Knowledge providers. It is never a ContextKey. */
export interface ErpKnowledgeRuntime {
    query: string;
}
